import { type RefObject, useCallback, useEffect, useRef } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useAppState, useNoticeBannersStackActions } from '@sg/garnish';

import { useTelemetry } from '@order/Telemetry';

import { checkIfFailureWasAlreadyShown, setFailureAlreadyShown } from '../../utils';

/**
 * We must only show the error when the app is visible.
 * Otherwise, the error will be marked as shown when the app is actually in the background.
 * Then the customer won't see it when they open the app again.
 */
export const useOrderPaymentStateErrorHandler = (props: Props) => {
  const { handleTimeslotUnavailable } = props;

  const { formatMessage } = useIntl();
  const { push: addNoticeBanner } = useNoticeBannersStackActions();
  const { track } = useTelemetry();

  const handleOnVisibleRef = useRef(async () => {});
  const handleOnVisible = useCallback(() => {
    void handleOnVisibleRef.current();
  }, []);

  const appState = useAppState({ onVisible: handleOnVisible });

  const onOrderPaymentStateError = useCallback(
    (params: Params) => {
      if (appState !== 'active') return;

      const { context } = params;
      const paymentStateError = context?.paymentState?.paymentStateError;
      const paymentIntentId = context?.paymentState?.paymentIntentId;

      const error = paymentStateError ?? formatMessage(messages.genericError);

      track('checkout.stripe-usage', { event: 'payment-state-error', error });

      if (paymentIntentId) void setFailureAlreadyShown(paymentIntentId);

      if (error === TIMESLOT_UNAVAILABLE_ERROR) {
        handleTimeslotUnavailable();
        return;
      }

      addNoticeBanner({ text: error, palette: 'caution' });
    },
    [appState, addNoticeBanner, formatMessage, handleTimeslotUnavailable, track],
  );

  return { handleOnVisibleRef, onOrderPaymentStateError };
};

/**
 * When the app is brought back from the background, we need to show the error.
 * Otherwise, the error will be shown while the customer isn't seeing it.
 */
export const useShowOrderPaymentStateErrorOnVisible = (
  paymentState: Params['context']['paymentState'],
  handleOnVisibleRef: RefObject<() => void>,
) => {
  const { push: addNoticeBanner } = useNoticeBannersStackActions();

  const showErrorOnVisible = useCallback(async () => {
    const id = paymentState?.paymentIntentId;
    const error = paymentState?.paymentStateError;

    if (!error) return;

    const hasAlreadyShownThisError = await checkIfFailureWasAlreadyShown(id);

    if (hasAlreadyShownThisError) return;

    addNoticeBanner({ text: error, palette: 'caution' });
    void setFailureAlreadyShown(id);
  }, [paymentState, addNoticeBanner]);

  useEffect(() => {
    handleOnVisibleRef.current = showErrorOnVisible;
  }, [handleOnVisibleRef, showErrorOnVisible]);
};

// ─── Constants ───────────────────────────────────────────────────────────────

const TIMESLOT_UNAVAILABLE_ERROR = 'That time is no longer available. Please select another.';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  genericError: {
    defaultMessage: 'There was an error processing your order. Please try again.', // prettier-ignore
    description: 'Ordering | Order payment state error',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  handleTimeslotUnavailable: () => void;
};

type Params = {
  context: {
    paymentState: {
      paymentIntentId: string | undefined;
      paymentStateError: string | undefined | null;
    };
  };
};
